import React, { useRef, useLayoutEffect, useState, useCallback } from 'react';
import { Box, Typography, IconButton, Tooltip, Chip, Paper } from '@mui/material';
import { Add, Delete, DragIndicator } from '@mui/icons-material';
import { BracketCard } from './BracketCard';

// ── Constants ──────────────────────────────────────────────────────────────────
const CARD_WIDTH = 200;
const CARD_GAP_Y = 24;      // vertical gap between cards in a column
const COL_GAP_X = 80;       // horizontal gap between stage columns
const HEADER_H = 44;        // stage header height
const COL_PADDING = 12;     // inner padding of column

// ── BracketCanvas ──────────────────────────────────────────────────────────────
// Single component: renders columns + an SVG layer on top for connector lines.
export const BracketCanvas = ({
  stages,
  linkingGame,
  canEdit = false,
  onAddGame, onEditGame, onDeleteGame, onLinkGame, onDeleteStage,
}) => {
  const containerRef = useRef(null);
  const cardRefs = useRef({}); // key: `${stageId}__${gameId}` → DOM el
  const [lines, setLines] = useState([]);
  const [dims, setDims] = useState({ w: 0, h: 0 });

  const refKey = (stageId, gameId) => `${stageId}__${gameId}`;

  const setCardRef = useCallback((stageId, gameId) => el => {
    cardRefs.current[refKey(stageId, gameId)] = el;
  }, []);

  // Stable ref to avoid stale closure in effect
  const stagesRef = useRef(stages);
  stagesRef.current = stages;
  const prevLinesRef = useRef('');

  // Compute lines WITHOUT setState — store in ref, only setState when changed
  const computeLines = useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const scrollLeft = container.scrollLeft;
    const scrollTop = container.scrollTop;

    // Rebuild stageMap from current stages
    const sMap = {};
    stagesRef.current.forEach(s => { sMap[s._id] = s; });

    const newLines = [];

    stagesRef.current.forEach(stage => {
      (stage.games || []).forEach(game => {
        if (!game.winnerFeedsInto?.gameEntryId) return;

        const fromEl = cardRefs.current[refKey(stage._id, game._id)];
        const toStage = sMap[game.winnerFeedsInto.stageId];
        const toGameId = game.winnerFeedsInto.gameEntryId;
        const toEl = toStage ? cardRefs.current[refKey(toStage._id, toGameId)] : null;

        if (!fromEl || !toEl) return;

        const fromRect = fromEl.getBoundingClientRect();
        const toRect = toEl.getBoundingClientRect();

        const x1 = Math.round(fromRect.right - containerRect.left + scrollLeft);
        const y1 = Math.round(fromRect.top + fromRect.height / 2 - containerRect.top + scrollTop);
        const x2 = Math.round(toRect.left - containerRect.left + scrollLeft);
        const y2 = Math.round(toRect.top + toRect.height / 2 - containerRect.top + scrollTop);

        const slot = game.winnerFeedsInto.slot || 1;
        const slotOffset = slot === 1 ? -Math.round(toRect.height / 4) : Math.round(toRect.height / 4);

        newLines.push({ x1, y1, x2, y2: y2 + slotOffset, slot, fromGameId: game._id });
      });
    });

    // Only update state if lines actually changed (prevents infinite loop)
    const serialized = JSON.stringify(newLines);
    if (serialized !== prevLinesRef.current) {
      prevLinesRef.current = serialized;
      setLines(newLines);
    }

    const w = container.scrollWidth;
    const h = container.scrollHeight;
    setDims(prev => (prev.w === w && prev.h === h ? prev : { w, h }));
  }, []); // stable — reads from refs only

  // Run once after mount + whenever stages data changes
  useLayoutEffect(() => {
    computeLines();
  }, [stages, computeLines]); // deps on stages data, not DOM positions

  // Also recompute on scroll (lines shift with scroll position)
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('scroll', computeLines, { passive: true });
    return () => container.removeEventListener('scroll', computeLines);
  }, [computeLines]);

  const sortedStages = [...stages].sort((a, b) => a.order - b.order);
return (
    <Box ref={containerRef} sx={{
      overflowX: 'auto', overflowY: 'auto',
      position: 'relative',
      pb: 2,
      '&::-webkit-scrollbar': { height: 6, width: 6 },
      '&::-webkit-scrollbar-thumb': { borderRadius: 3, bgcolor: 'action.selected' },
    }}>
      {/* Couche SVG des connecteurs — placée derrière les colonnes */}
      <svg
        style={{
          position: 'absolute', top: 0, left: 0, pointerEvents: 'none',
          width: dims.w, height: dims.h, overflow: 'visible', zIndex: 0,
        }}
      >
        <defs>
          <marker id="arrowhead" markerWidth="6" markerHeight="4" refX="6" refY="2" orient="auto">
            <polygon points="0 0, 6 2, 0 4" fill="#94a3b8" />
          </marker>
        </defs>
        {lines.map((l, i) => {
          const mx = (l.x1 + l.x2) / 2;
          // Courbe en S via courbe de Bézier cubique
          const path = `M ${l.x1} ${l.y1} C ${mx} ${l.y1}, ${mx} ${l.y2}, ${l.x2} ${l.y2}`;
          const slotColor = l.slot === 1 ? '#3b82f6' : '#8b5cf6';
          return (
            <g key={i}>
              {/* Ombre pour la profondeur */}
              <path d={path} fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth={4} strokeLinecap="round" />
              {/* Ligne principale */}
              <path
                d={path}
                fill="none"
                stroke={slotColor}
                strokeWidth={2}
                strokeLinecap="round"
                strokeDasharray="none"
                markerEnd="url(#arrowhead)"
                style={{ transition: 'stroke 0.2s' }}
              />
              {/* Étiquette de slot au point médian */}
              <circle cx={mx} cy={(l.y1 + l.y2) / 2} r={8} fill={slotColor} opacity={0.9} />
              <text x={mx} y={(l.y1 + l.y2) / 2 + 4} textAnchor="middle"
                fontSize="9" fill="white" fontWeight="bold" fontFamily="sans-serif">
                {l.slot}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Colonnes */}
      <Box sx={{ display: 'flex', gap: `${COL_GAP_X}px`, alignItems: 'flex-start', position: 'relative', zIndex: 1, minWidth: 'max-content' }}>
        {sortedStages.map((stage, stageIdx) => (
          <StageColumn
            key={stage._id}
            stage={stage}
            stageIdx={stageIdx}
            linkingGame={linkingGame}
            canEdit={canEdit}
            onAddGame={onAddGame}
            onEditGame={onEditGame}
            onDeleteGame={onDeleteGame}
            onLinkGame={onLinkGame}
            onDeleteStage={onDeleteStage}
            setCardRef={setCardRef}
          />
        ))}

        {/* Bouton d'ajout d'étape */}
        {canEdit && (
          <Box
            onClick={() => onAddGame(null, true)}
            sx={{
              width: 140, height: 72, border: '2px dashed', borderColor: 'divider',
              borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0, color: 'text.disabled',
              alignSelf: 'center',
              transition: 'all 0.2s',
              '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'action.hover' },
            }}>
            <Box textAlign="center">
              <Add />
              <Typography variant="caption" display="block">Ajouter une étape</Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ── StageColumn ────────────────────────────────────────────────────────────────
const StageColumn = ({ stage, stageIdx, linkingGame, canEdit = false, onAddGame, onEditGame, onDeleteGame, onLinkGame, onDeleteStage, setCardRef }) => {
  const isLinkingActive = !!linkingGame;
  const sorted = [...(stage.games || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

return (
    <Paper elevation={0} sx={{
      width: CARD_WIDTH + COL_PADDING * 2,
      border: '1px solid', borderColor: 'divider', borderRadius: 2,
      display: 'flex', flexDirection: 'column', flexShrink: 0,
      animation: 'fadeSlideIn 0.3s ease',
      '@keyframes fadeSlideIn': {
        from: { opacity: 0, transform: 'translateX(16px)' },
        to: { opacity: 1, transform: 'translateX(0)' },
      },
    }}>
      {/* En-tête */}
      <Box sx={{
        px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider',
        display: 'flex', alignItems: 'center', gap: 0.5,
        bgcolor: 'primary.main', borderRadius: '8px 8px 0 0',
        minHeight: HEADER_H,
      }}>
        <Typography variant="caption" fontWeight="bold" color="white" sx={{ flex: 1, fontSize: 12 }}>
          {stage.title}
        </Typography>
        <Chip label={sorted.length} size="small"
          sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', height: 18, fontSize: 10 }} />
        <Tooltip title={canEdit ? 'Ajouter un match' : 'Accès Super Administrateur requis'}>
          <span>
            <IconButton size="small" onClick={() => onAddGame(stage)} disabled={!canEdit}
              sx={{ color: 'white', p: 0.3, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}>
              <Add sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={canEdit ? 'Supprimer l\'étape' : 'Accès Super Administrateur requis'}>
          <span>
            <IconButton size="small" onClick={() => onDeleteStage(stage._id)} disabled={!canEdit}
              sx={{ color: 'rgba(255,255,255,0.6)', p: 0.3, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' } }}>
              <Delete sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Matchs */}
      <Box sx={{ p: `${COL_PADDING}px`, display: 'flex', flexDirection: 'column', gap: `${CARD_GAP_Y}px` }}>
        {sorted.length === 0 && (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="text.disabled">Aucun match pour l'instant</Typography>
          </Box>
        )}
        {sorted.map((g) => (
          <Box
            key={g._id}
            ref={setCardRef(stage._id, g._id)}
            sx={{
              '&:hover': isLinkingActive ? { outline: '2px solid', outlineColor: 'secondary.main', borderRadius: 2 } : {},
              transition: 'outline 0.1s',
            }}
          >
            <BracketCard
              gameEntry={g}
              stageOrder={stageIdx}
              canEdit={canEdit}
              onEdit={() => onEditGame(stage, g)}
              onDelete={(id) => onDeleteGame(stage._id, id)}
              onLinkClick={() => onLinkGame(stage, g)}
              isLinking={linkingGame?._id === g._id}
            />
          </Box>
        ))}
      </Box>
    </Paper>
  );
};